import { useState, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../firebase/config';
import {
    ensureMillis,
    isPOINullMetaType,
    MOCK_TENANT,
    mockAsyncCall,
    type POI,
    type POIType,
    type TenantApp,
    type TenantAppFirestoreDoc,
} from '@inloco/shared';

interface UseTenantAppFirestoreReturn {
    app: TenantApp | null;
    mapBlobUrl: string | null;
    isMapImageLoading: boolean;
    loading: boolean;
    error: string | null;
    getTenantAppByDomain: (domain: string) => Promise<TenantApp | null>;
    getTenantAppByDomainMock: () => Promise<TenantApp | null>;
}

export const useTenantAppFirestore = (): UseTenantAppFirestoreReturn => {
    const [app, setApp] = useState<TenantApp | null>(null);
    const [mapBlobUrl, setMapBlobUrl] = useState<string | null>(null);
    const [isMapImageLoading, setIsMapImageLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const e = 'errors.tenantError';

    // retrieve tenant by domain (mock for testing)
    const getTenantAppByDomainMock = useCallback(async (): Promise<TenantApp | null> => {
        try {
            setLoading(true);
            setError(null);

            const mock = await mockAsyncCall(MOCK_TENANT, 1500)

            setApp(mock)
            return mock;
        } catch (err: any) {
            setError(e);
            console.error('Firestore error:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [])


    // retrieve tenant by domain
    const getTenantAppByDomain = useCallback(async (domain: string): Promise<TenantApp | null> => {
        try {
            setLoading(true);
            setError(null);

            const appsRef = doc(firestore, 'apps', domain);

            const querySnapshot = await getDoc(appsRef);

            if (!querySnapshot.exists()) {
                setError(e);
                return null;
            }

            const appDoc = querySnapshot;
            const appData = { ...appDoc.data() } as TenantAppFirestoreDoc;
            const appId = appData.segmentId;

            const poisRef = collection(firestore, 'apps', appId, 'pois');
            const poisSnapshot = await getDocs(poisRef);

            const poisList = poisSnapshot.docs.map(doc => {
                const raw = doc.data() as any;
                const type = raw.type as POIType;

                // Alcuni POI, per contratto di modello (`POIType`), devono avere sempre `meta: null`.
                // Questo evita di propagare/salvare meta “sporchi” o inattesi quando il type non supporta meta
                // (e mantiene allineato il dato runtime con i types condivisi).
                const meta = isPOINullMetaType(type) ? null : raw.meta;

                return {
                    ...raw,
                    meta,
                    id: doc.id,
                } as POI;
            });

            const data = {
                ...appData,
                pois: poisList,
                // Il visitor client non deve ricevere la lista completa dei beacon.
                ble: [],
                createdAt: ensureMillis(appData.createdAt),
                updatedAt: ensureMillis(appData.updatedAt),
            } as TenantApp;

            setApp(data);
            return data;

        } catch (err: any) {
            setError(e);
            console.error('Firestore error:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {

        if (!app) return;

        const imageUrl = app.mapConfig.imageUrl;

        if (!imageUrl) return;

        let blobUrl: string | null = null;

        const downloadImage = async () => {
            setIsMapImageLoading(true);
            try {
                const response = await fetch(imageUrl);
                if (!response.ok) throw new Error('Download failed');

                const blob = await response.blob();
                blobUrl = URL.createObjectURL(blob);

                setMapBlobUrl(blobUrl);

            } catch (err: any) {
                setError(err);
                console.error("Map Image error:", err);
            } finally {
                setIsMapImageLoading(false);
            }
        };

        downloadImage();

        // CLEANUP: Fondamentale per liberare la RAM del browser
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
                setMapBlobUrl(null);
            }
        };

    }, [app]);

    return {
        app,
        mapBlobUrl,
        loading,
        isMapImageLoading,
        error,
        getTenantAppByDomain,
        getTenantAppByDomainMock
    };
};
